import { useEffect, useMemo, type ReactNode } from 'react';
import { getSessionToken } from '@room/frontend-sdk';
import { LoadingScreen } from './LoadingScreen';
import { useSession } from '../stores/session';
import { getLoginRedirectUrl, shouldRedirectToLogin } from '../utils/auth';
import { isBundleMode } from '../utils/bundleMode';

export interface AuthenticatedAppProps {
  /**
   * The app name displayed in loading screens
   */
  appName: string;

  /**
   * The authenticated content to render
   */
  children: ReactNode;

  /**
   * Optional custom loading message
   */
  loadingMessage?: string;

  /**
   * Optional custom redirect message
   */
  redirectMessage?: string;
}

/**
 * AuthenticatedApp - Wrapper component that handles authentication flow
 *
 * This component eliminates boilerplate by:
 * 1. Checking user session automatically
 * 2. Showing loading screen during verification
 * 3. Redirecting to login if not authenticated
 * 4. Rendering children only when authenticated
 *
 * Usage:
 * ```tsx
 * export function App() {
 *   return (
 *     <AuthenticatedApp appName="Settings">
 *       <SettingsPage />
 *     </AuthenticatedApp>
 *   );
 * }
 * ```
 */
export function AuthenticatedApp({
  appName,
  children,
  loadingMessage,
  redirectMessage,
}: AuthenticatedAppProps) {
  const { isAuthenticated, isLoading, hasCheckedSession, error, refresh } = useSession();
  const returnTo = useMemo(() => window.location.pathname + window.location.search, []);

  const debugDetails = (
    <ul style={{ margin: 0, paddingLeft: '16px' }}>
      <li>{`hasToken: ${Boolean(getSessionToken())}`}</li>
      <li>{`isLoading: ${isLoading}`}</li>
      <li>{`hasCheckedSession: ${hasCheckedSession}`}</li>
      <li>{`isAuthenticated: ${isAuthenticated}`}</li>
      {error ? <li>{`error: ${error.message}`}</li> : null}
    </ul>
  );

  useEffect(() => {
    if (hasCheckedSession && !isLoading && !isAuthenticated) {
      if (shouldRedirectToLogin(window.location.pathname)) {
        window.location.replace(getLoginRedirectUrl(returnTo));
      }
    }
  }, [hasCheckedSession, isAuthenticated, isLoading, returnTo]);

  if (!hasCheckedSession || isLoading) {
    return (
      <LoadingScreen
        title={`Loading ${appName}`}
        message={loadingMessage ?? `Checking your session before opening ${appName.toLowerCase()}.`}
        details={debugDetails}
      />
    );
  }

  if (!isAuthenticated) {
    const redirectBlocked = isBundleMode();
    return (
      <LoadingScreen
        title={redirectBlocked ? 'Sign in required' : 'Redirecting'}
        message={
          redirectMessage ??
          (redirectBlocked
            ? 'Please sign in from the home screen before opening this app.'
            : 'Taking you to the login screen.')
        }
        details={debugDetails}
        actions={
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {error ? (
              <button
                type="button"
                onClick={() => refresh()}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Retry session check
              </button>
            ) : null}
            {redirectBlocked ? (
              <button
                type="button"
                onClick={() => window.open('/login', '_blank', 'noopener,noreferrer')}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #1d4ed8',
                  background: '#1d4ed8',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Open login
              </button>
            ) : null}
          </div>
        }
      />
    );
  }

  return <>{children}</>;
}
